/**
 * Tenant → People → Affiliates (TMMPDM-0042). Read-only list. The status filter is in the URL
 * and sent to the server. "Held for review" comes only from `payoutsHeldForReview`.
 */
import { useEffect, useState } from 'react';
import {
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useCopy } from '@/hooks/useCopy';
import { oneOf, useUrlState, type UrlSchema } from '@/hooks/useUrlState';
import { EmptyState, ErrorState } from '@/components/page';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { Amount, HelpPopover, stickyTableContainerSx } from '@/components/ui';
import { adminApi } from '@/api/tmm/admin';
import { AFFILIATE_STATUSES, type AffiliateListRow, type AffiliateStatus } from '@/api/tmm/types';
import {
  AffiliateStatusChip,
  AgreementPlanCell,
  HeldForReviewChip,
  Last30Lines,
} from './affiliateShared';

type ListView = { status: AffiliateStatus | undefined };
const VIEW: UrlSchema<ListView> = {
  status: { default: undefined, parse: oneOf(AFFILIATE_STATUSES) },
};

interface TenantAffiliatesPanelProps {
  slug: string;
  resellerId: number;
}

export function TenantAffiliatesPanel({ slug, resellerId }: TenantAffiliatesPanelProps) {
  const { t } = useCopy();
  const [view, setView] = useUrlState(VIEW);
  const status = view.status;
  const [reloadToken, setReloadToken] = useState(0);
  const [rows, setRows] = useState<AffiliateListRow[] | null>(null);
  const [error, setError] = useState<{ cause: unknown } | null>(null);
  const help = t(
    'affiliates.list.help',
    'Affiliates belong to this tenant and are managed by its reseller. You can view them, and pause or end one for cause.',
  );

  useEffect(() => {
    let cancelled = false;
    adminApi
      .listAffiliates(slug, status !== undefined ? { status } : undefined)
      .then((list) => {
        if (cancelled) return;
        setRows(list.affiliates);
        setError(null);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setRows(null);
          setError({ cause: err });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [slug, status, reloadToken]);

  if (error) {
    return (
      <ErrorState
        error={error.cause}
        message={t('affiliates.list.loadError', 'Could not load affiliates.')}
        onRetry={() => setReloadToken((n) => n + 1)}
      />
    );
  }

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
        <Typography variant="h2" component="h2">
          {t('affiliates.list.title', 'Affiliates')}
        </Typography>
        <HelpPopover label={t('affiliates.list.title', 'Affiliates')} short={help} />
      </Stack>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={status ?? 'ALL'}
        onChange={(_event, value: AffiliateStatus | 'ALL' | null) => {
          if (value === null) return;
          setView({ status: value === 'ALL' ? undefined : value });
        }}
        aria-label={t('affiliates.list.filterAria', 'Affiliate status')}
      >
        <ToggleButton value="ALL">{t('affiliates.filter.all', 'All')}</ToggleButton>
        {AFFILIATE_STATUSES.map((value) => (
          <ToggleButton key={value} value={value}>
            <AffiliateStatusWord status={value} />
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      {rows === null ? (
        <LoadingSpinner />
      ) : (
        <AffiliateTable
          resellerId={resellerId}
          rows={rows}
          filtered={status !== undefined}
          onClear={() => setView({ status: undefined })}
        />
      )}
    </Stack>
  );
}

function AffiliateStatusWord({ status }: { status: AffiliateStatus }) {
  const { t } = useCopy();
  const labels: Record<AffiliateStatus, string> = {
    APPLIED: 'Applied',
    ACTIVE: 'Active',
    PAUSED: 'Paused',
    ENDED: 'Ended',
  };
  return t(`affiliates.status.${status}`, labels[status]);
}

function AffiliateTable({
  resellerId,
  rows,
  filtered,
  onClear,
}: {
  resellerId: number;
  rows: AffiliateListRow[];
  filtered: boolean;
  onClear: () => void;
}) {
  const { t } = useCopy();
  return (
    <TableContainer component={Paper} variant="outlined" sx={stickyTableContainerSx}>
      <Table size="small" stickyHeader aria-label={t('affiliates.list.aria', 'Affiliates')}>
        <TableHead>
          <TableRow>
            <TableCell>{t('affiliates.col.name', 'Name')}</TableCell>
            <TableCell>{t('affiliates.col.email', 'Email')}</TableCell>
            <TableCell>{t('affiliates.col.status', 'Status')}</TableCell>
            <TableCell>{t('affiliates.col.plan', 'Plan')}</TableCell>
            <TableCell align="right">
              {t('affiliates.col.referredActive', 'Referred active')}
            </TableCell>
            <TableCell>{t('affiliates.col.last30', 'Last 30 days')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                <EmptyState
                  title={
                    filtered
                      ? t('affiliates.list.filteredEmpty', 'No affiliates match this filter.')
                      : t('affiliates.list.empty', 'No affiliates yet.')
                  }
                  {...(filtered ? { filtered: { onClear } } : {})}
                />
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>
                  <Link
                    component={RouterLink}
                    to={`/partners/${resellerId}/affiliates/${row.id}`}
                    underline="hover"
                  >
                    {row.displayName}
                  </Link>
                </TableCell>
                <TableCell>{row.email}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
                    <AffiliateStatusChip status={row.status} />
                    {row.payoutsHeldForReview ? <HeldForReviewChip /> : null}
                  </Stack>
                </TableCell>
                <TableCell>
                  <AgreementPlanCell agreement={row.agreement} />
                </TableCell>
                <TableCell align="right">
                  <Amount count={row.referredActive} />
                </TableCell>
                <TableCell>
                  <Last30Lines rows={row.performance} />
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
